// TGE → STAGIL migration API (audit / migrate / run history).
//
// Shapes mirror the backend responses exactly:
// - POST /api/data/tge/audit      (api/routes/tge_migration.py::tge_audit, sync, slow)
// - POST /api/data/tge/migrate    (202 {run_id} | 409 already running | 400 no tokens)
// - GET  /api/data/tge/runs       (last runs, newest first)
// - GET  /api/data/tge/runs/{id}  (summary + params + report; report null while running)
// - GET|PUT /api/data/tge/column-map  (column map saved per grid → table; {} clears it)
//
// Callers branch on status codes and read the FastAPI `detail`; ApiError comes
// from lib/http.ts so `instanceof` works across libs.

import { ApiError } from '@/lib/http'

export { ApiError }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  })
  if (!res.ok) {
    let detail = await res.text()
    try {
      const parsed: unknown = JSON.parse(detail)
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        typeof (parsed as { detail?: unknown }).detail === 'string'
      ) {
        detail = (parsed as { detail: string }).detail
      }
    } catch {
      // keep raw text
    }
    throw new ApiError(res.status, detail || `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

// ---- request bodies ---------------------------------------------------------------

export type TgeAuditParams = {
  grid_id: number
  table_id?: number | null // null → backend default (27214→4475, 27807→4418); custom grids pass it
  config_id?: number | null
  // TGE column → STAGIL column id ("field-123") or title; "" = don't migrate that column
  overrides?: Record<string, string> | null
}

export type TgeMigrateParams = TgeAuditParams & {
  issue_keys?: string[] | null // null → whole grid
  dry_run?: boolean // backend default: true
}

// ---- audit ------------------------------------------------------------------------

export type TgePerIssue = {
  issue_id: number
  issue_key: string | null
  rows: number
  size_estimate: number
  oversize: boolean
}

/** A column of the STAGIL table, as the column-map editor offers it. */
export type TgeStagilField = {
  id: string
  title: string
  type: string
  options?: string[] // option labels of a select column
  config?: Record<string, unknown> // the column as STAGIL gave it (decimal places, limits, ...)
}

export type TgeAuditResult = {
  grid_id: number
  table_id: number
  fields_id_used: number
  rowcount: number | null // null: TGE /rowcount answered an unknown shape (see warnings)
  issues: number
  rows_total: number
  column_map: Record<string, string>
  unmatched_columns: string[]
  stagil_fields: TgeStagilField[]
  oversize: TgePerIssue[]
  missing_dc_keys: number[]
  per_issue: TgePerIssue[]
  warnings: string[]
}

/** Long request (reads the whole TGE grid from DC) — deliberately no client timeout. */
export function runTgeAudit(params: TgeAuditParams): Promise<TgeAuditResult> {
  return request<TgeAuditResult>('/data/tge/audit', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// ---- migrate + runs -----------------------------------------------------------------

export type TgeRunStatus = 'running' | 'ok' | 'partial' | 'error'

export type TgeOutcome =
  | 'ok'
  | 'ok_dry'
  | 'empty_source'
  | 'size_exceeded'
  | 'skipped_missing_issue'
  | 'skipped_missing_dc_key'
  | 'skipped_not_on_dc' // a requested key Jira DC does not know (typo / Cloud-only issue)
  | 'no_mapped_values' // rows carry values only in unmapped columns — never PUT (would blank the table)
  | 'verify_mismatch'
  | 'error'

export type TgeRunTotals = {
  issues: number
  rows: number
  outcomes: Partial<Record<TgeOutcome, number>>
  warnings: number
}

/** Written by the run as it goes; the last state stays on the finished run. */
export type TgeRunProgress = {
  stage: 'reading' | 'resolving' | 'writing' | 'done'
  done: number
  total: number | null // reading: TGE's own total, an estimate (null when it gives none)
  outcomes: Partial<Record<TgeOutcome, number>>
}

export type TgeRunSummary = {
  id: number
  grid_id: number
  table_id: number
  dry_run: boolean
  status: TgeRunStatus
  started_at: string
  finished_at: string | null
  totals: TgeRunTotals | null // null while running
  progress: TgeRunProgress | null // null until the run reports its first stage
}

export type TgeReportIssue = {
  issue_id: number | null // null for a requested key that is not on Jira DC
  issue_key: string | null
  rows: number
  outcome: TgeOutcome
  size_estimate?: number
  verified_rows?: number
  error?: string
  // runs on a few keys only: cells sent and rows STAGIL read back (no -html copies), ≤5 rows
  sent?: Record<string, unknown>[]
  stored?: Record<string, unknown>[]
}

/** Per-issue report on success; `{error}` when the whole run body blew up. */
export type TgeRunReport =
  | {
      issues: TgeReportIssue[]
      warnings: string[]
      column_map: Record<string, string>
      unmatched_columns: string[]
      fields_id_used: number
    }
  // needs_column_map: stopped because no TGE column is mapped — the cure is the column map
  | { error: string; needs_column_map?: boolean }

export type TgeRunDetail = TgeRunSummary & {
  params: Record<string, unknown> | null
  report: TgeRunReport | null // null while running
}

// ---- column map saved per grid → table ----------------------------------------------

/** TGE column → STAGIL column id; "" = not migrated. Audit and migrate without explicit
 * overrides use the saved one. */
export type TgeColumnMapSaved = {
  grid_id: number
  table_id: number
  overrides: Record<string, string>
  updated_by: string | null // null: nothing saved, columns match by name
  updated_at: string | null
}

export function getTgeColumnMap(gridId: number, tableId: number): Promise<TgeColumnMapSaved> {
  return request<TgeColumnMapSaved>(`/data/tge/column-map?grid_id=${gridId}&table_id=${tableId}`)
}

/** Saves the map for grid → table; {} removes it. */
export function saveTgeColumnMap(
  gridId: number,
  tableId: number,
  overrides: Record<string, string>,
): Promise<TgeColumnMapSaved> {
  return request<TgeColumnMapSaved>('/data/tge/column-map', {
    method: 'PUT',
    body: JSON.stringify({ grid_id: gridId, table_id: tableId, overrides }),
  })
}

export function startTgeMigration(params: TgeMigrateParams): Promise<{ run_id: number }> {
  return request<{ run_id: number }>('/data/tge/migrate', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/** A run that failed as a whole has no totals. Runs stored before 2026-11-01 carry `{}`
 * there instead of null — read as totals, it crashed the run report (run #20). */
function withTotals<R extends TgeRunSummary>(r: R): R {
  return r.totals !== null && typeof r.totals?.outcomes === 'object' ? r : { ...r, totals: null }
}

const EMPTY_MAP_ERROR = 'ValueError: no TGE column matches a STAGIL column'

/** Runs #20/#21 stopped on an empty column map before the backend flagged it: read the
 * flag off their error text, so their report offers the column map too. */
function withMapFlag(d: TgeRunDetail): TgeRunDetail {
  const rep = d.report
  if (rep === null || !('error' in rep) || rep.needs_column_map !== undefined) return d
  return rep.error.startsWith(EMPTY_MAP_ERROR) ? { ...d, report: { ...rep, needs_column_map: true } } : d
}

export async function getTgeRuns(limit = 10): Promise<TgeRunSummary[]> {
  return (await request<TgeRunSummary[]>(`/data/tge/runs?limit=${limit}`)).map(withTotals)
}

export async function getTgeRun(id: number): Promise<TgeRunDetail> {
  return withMapFlag(withTotals(await request<TgeRunDetail>(`/data/tge/runs/${id}`)))
}
